import { useEffect, useState } from 'react';
import { fetchImageAsBlobUrl } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Renders a photograph fetched through an authenticated endpoint as a
 * revocable object URL. R2 objects are private, so they can never be loaded
 * through a public <img> src; the object URL is revoked when the image
 * unmounts or the source changes.
 */
export function AuthenticatedPhoto({
  src,
  alt,
  className,
  placeholderClassName,
}: {
  src: string;
  alt?: string;
  className?: string;
  placeholderClassName?: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let revoke: string | null = null;
    setUrl(null);
    setFailed(false);
    fetchImageAsBlobUrl(src)
      .then((u) => {
        if (!active) {
          URL.revokeObjectURL(u);
          return;
        }
        revoke = u;
        setUrl(u);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [src]);

  if (failed) {
    return (
      <div className={cn('flex items-center justify-center bg-muted', placeholderClassName)}>
        <img src="/assests/photo-placeholder.svg" alt="Unavailable" className="max-h-full max-w-full object-contain opacity-60" />
      </div>
    );
  }

  if (!url) {
    return <div className={cn('animate-pulse bg-muted', placeholderClassName)} aria-busy="true" />;
  }

  return <img src={url} alt={alt || ''} className={className} loading="lazy" />;
}
